/**
 * @module browser-storage-adapter
 * @description Implements the local-first admin adapter used for demos,
 * prototyping, and Storybook scenarios. The adapter stores admin-facing state
 * in guarded browser storage and mirrors the shared admin contract.
 */

import {
  getBrowserLocalStorage,
  isBrowserStorageQuotaError,
} from "../browser-storage";
import { AdminError } from "../errors";
import { warnRuntimeAdapterMock } from "../runtime-warning";
import type {
  AdminAdapter,
  AdminListOptions,
  AdminMetrics,
  AdminRole,
  AdminRoleInput,
  AdminTeam,
  AdminTeamInput,
  ManagedUser,
} from "./interface";

// Reuse the auth adapter key to sync user data.
const USERS_KEY = "geenius_users";
const SESSION_KEY = "geenius_session";
const ROLES_KEY = "geenius_admin_roles";
const TEAMS_KEY = "geenius_admin_teams";

interface StoredAdminUser {
  id: string;
  email?: string;
  name?: string;
  image?: string;
  passwordHash?: string;
  isBanned?: boolean;
  roleIds?: string[];
  teamIds?: string[];
  plan?: string;
  createdAt?: string;
  lastLoginAt?: string;
}

type StoredAdminRole = AdminRole;
type StoredAdminTeam = AdminTeam;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

function isStoredAdminUser(value: unknown): value is StoredAdminUser {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "string" &&
    (value.email === undefined || typeof value.email === "string") &&
    (value.name === undefined || typeof value.name === "string") &&
    (value.image === undefined || typeof value.image === "string") &&
    (value.passwordHash === undefined ||
      typeof value.passwordHash === "string") &&
    (value.isBanned === undefined || typeof value.isBanned === "boolean") &&
    (value.roleIds === undefined || isStringArray(value.roleIds)) &&
    (value.teamIds === undefined || isStringArray(value.teamIds)) &&
    (value.plan === undefined || typeof value.plan === "string") &&
    (value.createdAt === undefined || typeof value.createdAt === "string") &&
    (value.lastLoginAt === undefined || typeof value.lastLoginAt === "string")
  );
}

function isStoredAdminRole(value: unknown): value is StoredAdminRole {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "string" &&
    typeof value.name === "string" &&
    (value.description === undefined ||
      typeof value.description === "string") &&
    isStringArray(value.permissions) &&
    (value.isSystem === undefined || typeof value.isSystem === "boolean") &&
    (value.createdAt === undefined || typeof value.createdAt === "string") &&
    (value.updatedAt === undefined || typeof value.updatedAt === "string")
  );
}

function isStoredAdminTeam(value: unknown): value is StoredAdminTeam {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "string" &&
    typeof value.name === "string" &&
    (value.description === undefined ||
      typeof value.description === "string") &&
    isStringArray(value.memberUserIds) &&
    isStringArray(value.roleIds) &&
    (value.createdAt === undefined || typeof value.createdAt === "string") &&
    (value.updatedAt === undefined || typeof value.updatedAt === "string")
  );
}

function isArrayOf<T>(
  value: unknown,
  predicate: (item: unknown) => item is T,
): value is T[] {
  return Array.isArray(value) && value.every(predicate);
}

function readStoredUsers(): StoredAdminUser[] {
  return readStoredCollection(USERS_KEY, isStoredAdminUser, "users");
}

function writeStoredUsers(users: StoredAdminUser[]): void {
  writeStoredCollection(USERS_KEY, users, "users");
}

function readStoredRoles(): StoredAdminRole[] {
  return readStoredCollection(ROLES_KEY, isStoredAdminRole, "roles");
}

function writeStoredRoles(roles: StoredAdminRole[]): void {
  writeStoredCollection(ROLES_KEY, roles, "roles");
}

function readStoredTeams(): StoredAdminTeam[] {
  return readStoredCollection(TEAMS_KEY, isStoredAdminTeam, "teams");
}

function writeStoredTeams(teams: StoredAdminTeam[]): void {
  writeStoredCollection(TEAMS_KEY, teams, "teams");
}

function readStoredCollection<T>(
  key: string,
  predicate: (item: unknown) => item is T,
  label: string,
): T[] {
  try {
    const storage = getBrowserLocalStorage();
    const raw = storage.getItem(key);
    const parsed: unknown = JSON.parse(raw || "[]");
    if (!isArrayOf(parsed, predicate)) {
      throw new AdminError(
        `Invalid ${label} payload in browser storage`,
        "OPERATION_FAILED",
      );
    }
    return parsed;
  } catch (error) {
    if (error instanceof AdminError) {
      throw error;
    }
    if (error instanceof SyntaxError) {
      throw new AdminError(
        `Failed to parse ${label} from browser storage`,
        "OPERATION_FAILED",
        error,
      );
    }
    throw new AdminError(
      "Failed to read from browser storage",
      "OPERATION_FAILED",
      error,
    );
  }
}

function writeStoredCollection<T>(
  key: string,
  items: T[],
  label: string,
): void {
  try {
    const storage = getBrowserLocalStorage();
    storage.setItem(key, JSON.stringify(items));
  } catch (error) {
    if (isBrowserStorageQuotaError(error)) {
      throw new AdminError(
        "browser storage quota exceeded",
        "OPERATION_FAILED",
        error,
      );
    }
    throw new AdminError(
      `Failed to write ${label} to browser storage`,
      "OPERATION_FAILED",
      error,
    );
  }
}

function clearStoredSessionForUser(userId: string): void {
  try {
    const storage = getBrowserLocalStorage();
    const raw = storage.getItem(SESSION_KEY);
    if (!raw) {
      return;
    }

    const parsed: unknown = JSON.parse(raw);
    if (isRecord(parsed) && parsed.userId === userId) {
      storage.removeItem(SESSION_KEY);
    }
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new AdminError(
        "Failed to parse session from browser storage",
        "OPERATION_FAILED",
        error,
      );
    }
    throw new AdminError(
      "Failed to clear deleted user session from browser storage",
      "OPERATION_FAILED",
      error,
    );
  }
}

function unique(items: readonly string[]): string[] {
  return [...new Set(items)];
}

function applyListOptions<T>(
  items: T[],
  options: AdminListOptions | undefined,
  getSearchText: (item: T) => Array<string | undefined>,
): T[] {
  let filtered = items;

  if (options?.search) {
    const query = options.search.toLowerCase();
    filtered = filtered.filter((item) =>
      getSearchText(item).some((value) => value?.toLowerCase().includes(query)),
    );
  }

  const offset = options?.offset ?? 0;
  const limit = options?.limit ?? 50;
  return filtered.slice(offset, offset + limit);
}

function makeStoredRole(
  input: AdminRoleInput,
  existing?: AdminRole,
): AdminRole {
  const now = new Date().toISOString();
  return {
    id: input.id ?? existing?.id ?? crypto.randomUUID(),
    name: input.name,
    description: input.description,
    permissions: unique(input.permissions ?? existing?.permissions ?? []),
    isSystem: input.isSystem ?? existing?.isSystem,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
}

function makeStoredTeam(
  input: AdminTeamInput,
  existing?: AdminTeam,
): AdminTeam {
  const now = new Date().toISOString();
  return {
    id: input.id ?? existing?.id ?? crypto.randomUUID(),
    name: input.name,
    description: input.description,
    memberUserIds: unique(input.memberUserIds ?? existing?.memberUserIds ?? []),
    roleIds: unique(input.roleIds ?? existing?.roleIds ?? []),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
}

function assertKnownTeamReferences(
  team: AdminTeam,
  users: StoredAdminUser[],
  roles: StoredAdminRole[],
): void {
  const userIds = new Set(users.map((user) => user.id));
  const roleIds = new Set(roles.map((role) => role.id));
  const missingUserId = team.memberUserIds.find(
    (userId) => !userIds.has(userId),
  );
  const missingRoleId = team.roleIds.find((roleId) => !roleIds.has(roleId));

  if (missingUserId) {
    throw new AdminError(
      `Cannot upsert team with unknown member user "${missingUserId}"`,
      "USER_NOT_FOUND",
    );
  }

  if (missingRoleId) {
    throw new AdminError(
      `Cannot upsert team with unknown role "${missingRoleId}"`,
      "OPERATION_FAILED",
    );
  }
}

function upsertById<T extends { id: string }>(items: T[], item: T): T[] {
  const index = items.findIndex((candidate) => candidate.id === item.id);
  if (index < 0) {
    return [...items, item];
  }
  const next = [...items];
  next[index] = item;
  return next;
}

/**
 * Creates the mock browser-storage-backed admin adapter used in demos and Storybook.
 *
 * @returns An admin adapter implementation that reuses the local auth dataset for management flows.
 * @throws {AdminError} When admin-facing browser storage reads or writes fail.
 */
export function createLocalStorageAdminAdapter(): AdminAdapter {
  if (
    typeof process !== "undefined" &&
    process.env?.NODE_ENV === "production"
  ) {
    warnRuntimeAdapterMock(
      "[@geenius/adapters] browser storage admin adapter is a MOCK for prototyping only. " +
        "Do NOT use in production.",
    );
  }

  function getUsers(): ManagedUser[] {
    return readStoredUsers().map((u) => ({
      id: u.id,
      email: u.email,
      name: u.name,
      image: u.image,
      isActive: true,
      isBanned: u.isBanned ?? false,
      roleIds: unique(u.roleIds ?? []),
      teamIds: unique(u.teamIds ?? []),
      plan: u.plan ?? "free",
      createdAt: u.createdAt,
      lastLoginAt: u.lastLoginAt,
    }));
  }

  return {
    async listUsers(options?) {
      return applyListOptions(getUsers(), options, (user) => [
        user.email,
        user.name,
        user.plan,
      ]);
    },

    async getUser(id) {
      const users = getUsers();
      return users.find((u) => u.id === id) || null;
    },

    async banUser(id) {
      const data = readStoredUsers();
      const user = data.find((u) => u.id === id);
      if (!user || user.isBanned === true) return false;
      user.isBanned = true;
      writeStoredUsers(data);
      return true;
    },

    async unbanUser(id) {
      const data = readStoredUsers();
      const user = data.find((u) => u.id === id);
      if (!user || user.isBanned !== true) return false;
      user.isBanned = false;
      writeStoredUsers(data);
      return true;
    },

    async deleteUser(id) {
      const data = readStoredUsers();
      const index = data.findIndex((u) => u.id === id);
      if (index < 0) return false;
      data.splice(index, 1);
      writeStoredUsers(data);

      const teams = readStoredTeams().map((team) => ({
        ...team,
        memberUserIds: team.memberUserIds.filter((userId) => userId !== id),
      }));
      writeStoredTeams(teams);
      clearStoredSessionForUser(id);
      return true;
    },

    async listRoles(options?) {
      return applyListOptions(readStoredRoles(), options, (role) => [
        role.name,
        role.description,
        role.permissions.join(" "),
      ]);
    },

    async getRole(id) {
      return readStoredRoles().find((role) => role.id === id) ?? null;
    },

    async upsertRole(input) {
      const roles = readStoredRoles();
      const existing = input.id
        ? roles.find((role) => role.id === input.id)
        : undefined;
      const role = makeStoredRole(input, existing);
      writeStoredRoles(upsertById(roles, role));
      return role;
    },

    async deleteRole(id) {
      const roles = readStoredRoles();
      const nextRoles = roles.filter((role) => role.id !== id);
      if (nextRoles.length === roles.length) return false;

      writeStoredRoles(nextRoles);
      writeStoredUsers(
        readStoredUsers().map((user) => ({
          ...user,
          roleIds: (user.roleIds ?? []).filter((roleId) => roleId !== id),
        })),
      );
      writeStoredTeams(
        readStoredTeams().map((team) => ({
          ...team,
          roleIds: team.roleIds.filter((roleId) => roleId !== id),
        })),
      );
      return true;
    },

    async assignUserRole(userId, roleId) {
      if (!readStoredRoles().some((role) => role.id === roleId)) return false;

      const users = readStoredUsers();
      const user = users.find((candidate) => candidate.id === userId);
      if (!user) return false;

      const roleIds = unique(user.roleIds ?? []);
      if (roleIds.includes(roleId)) return false;

      user.roleIds = [...roleIds, roleId];
      writeStoredUsers(users);
      return true;
    },

    async removeUserRole(userId, roleId) {
      const users = readStoredUsers();
      const user = users.find((candidate) => candidate.id === userId);
      if (!user?.roleIds?.includes(roleId)) return false;

      user.roleIds = user.roleIds.filter(
        (assignedRoleId) => assignedRoleId !== roleId,
      );
      writeStoredUsers(users);
      return true;
    },

    async listTeams(options?) {
      return applyListOptions(readStoredTeams(), options, (team) => [
        team.name,
        team.description,
      ]);
    },

    async getTeam(id) {
      return readStoredTeams().find((team) => team.id === id) ?? null;
    },

    async upsertTeam(input) {
      const teams = readStoredTeams();
      const existing = input.id
        ? teams.find((team) => team.id === input.id)
        : undefined;
      const team = makeStoredTeam(input, existing);
      const users = readStoredUsers();
      assertKnownTeamReferences(team, users, readStoredRoles());
      writeStoredTeams(upsertById(teams, team));

      const memberIds = new Set(team.memberUserIds);
      writeStoredUsers(
        users.map((user) => {
          const teamIds = unique(user.teamIds ?? []);
          const shouldHaveTeam = memberIds.has(user.id);
          return {
            ...user,
            teamIds: shouldHaveTeam
              ? unique([...teamIds, team.id])
              : teamIds.filter((teamId) => teamId !== team.id),
          };
        }),
      );
      return team;
    },

    async deleteTeam(id) {
      const teams = readStoredTeams();
      const nextTeams = teams.filter((team) => team.id !== id);
      if (nextTeams.length === teams.length) return false;

      writeStoredTeams(nextTeams);
      writeStoredUsers(
        readStoredUsers().map((user) => ({
          ...user,
          teamIds: (user.teamIds ?? []).filter((teamId) => teamId !== id),
        })),
      );
      return true;
    },

    async addTeamMember(teamId, userId) {
      const users = readStoredUsers();
      const user = users.find((candidate) => candidate.id === userId);
      if (!user) return false;

      const teams = readStoredTeams();
      const team = teams.find((candidate) => candidate.id === teamId);
      if (!team || team.memberUserIds.includes(userId)) return false;

      team.memberUserIds = [...team.memberUserIds, userId];
      user.teamIds = unique([...(user.teamIds ?? []), teamId]);
      writeStoredTeams(teams);
      writeStoredUsers(users);
      return true;
    },

    async removeTeamMember(teamId, userId) {
      const teams = readStoredTeams();
      const team = teams.find((candidate) => candidate.id === teamId);
      if (!team?.memberUserIds.includes(userId)) return false;

      const users = readStoredUsers();
      const user = users.find((candidate) => candidate.id === userId);
      team.memberUserIds = team.memberUserIds.filter(
        (memberId) => memberId !== userId,
      );
      if (user?.teamIds) {
        user.teamIds = user.teamIds.filter(
          (assignedTeamId) => assignedTeamId !== teamId,
        );
      }
      writeStoredTeams(teams);
      writeStoredUsers(users);
      return true;
    },

    async assignTeamRole(teamId, roleId) {
      if (!readStoredRoles().some((role) => role.id === roleId)) return false;

      const teams = readStoredTeams();
      const team = teams.find((candidate) => candidate.id === teamId);
      if (!team || team.roleIds.includes(roleId)) return false;

      team.roleIds = [...team.roleIds, roleId];
      writeStoredTeams(teams);
      return true;
    },

    async removeTeamRole(teamId, roleId) {
      const teams = readStoredTeams();
      const team = teams.find((candidate) => candidate.id === teamId);
      if (!team?.roleIds.includes(roleId)) return false;

      team.roleIds = team.roleIds.filter(
        (assignedRoleId) => assignedRoleId !== roleId,
      );
      writeStoredTeams(teams);
      return true;
    },

    async getMetrics(): Promise<AdminMetrics> {
      const users = getUsers();
      const today = new Date().toISOString().split("T")[0] ?? "";
      const newToday = users.filter((u) =>
        u.createdAt?.startsWith(today),
      ).length;

      return {
        totalUsers: users.length,
        activeUsers: users.filter((u) => u.isActive && !u.isBanned).length,
        newUsersToday: newToday,
      };
    },
  };
}
